/**
 * Dashboard Data Hook
 * Combines engagement info with SP-backed analytics data for the dashboard
 */

import { useMemo, useEffect, useState, useCallback, useRef } from 'react'
import { useEngagementApi } from '@/hooks/use-engagement-api'
import { useEngagementDashboardApi, type EngagementDashboardFilters } from '@/hooks/use-engagement-dashboard-api'
import type { DashboardData, TeamHoursResponse, TeamMemberHours, HoursReportRow, PortfolioTeamHoursResponse, PortfolioFinanceData, PortfolioFinanceEngagement, EngagementLeadHours, KpiCardsRow, MonthlyChartRow, EngagementTableRow, UtilizationDataPoint, UtilizationFilterOption, UtilizationResponse } from '@/types/ui/dashboard'
import { calculateRevenueTrend } from '@/lib/utils/dashboard-utils'
import { type WorkerTypeData } from '@/lib/adapters/bold-report-adapter'
import { parseDateOnly } from '@/lib/utils'
import { ENGAGEMENT_STATUS } from '@/lib/constants/engagement-codes'


/**
 * Build the EngagementDashboardFilters for an individual-engagement monthly
 * data fetch. Normalizes engagementInfo's dates from the search response —
 * which arrive as full ISO datetimes with a timezone offset (e.g.
 * "2025-06-02T00:00:00+00:00") — down to the YYYY-MM-DD format the filter
 * contract documents. Without the slice, ASP.NET model binds the offset-bearing
 * string as a UTC DateTime which can shift the effective boundary by a day on
 * non-UTC servers. The first 10 characters of the search format are guaranteed
 * to be the calendar date. Open-ended engagements (null endDate) fall back to
 * today so the SP returns data through the current month.
 */
function buildIndividualMonthlyFilters(
  selectedEngagementId: number | undefined,
  engagementInfo: { startDate?: string | null; endDate?: string | null } | null,
): EngagementDashboardFilters | null {
  if (!selectedEngagementId || !engagementInfo?.startDate) return null
  const todayYmd = new Date().toISOString().slice(0, 10)
  return {
    engagementId: selectedEngagementId,
    effectiveFrom: engagementInfo.startDate.slice(0, 10),
    effectiveTo: (engagementInfo.endDate ?? todayYmd).slice(0, 10),
  }
}

/**
 * Map a spEngagementHoursReport row (GET /api/engagement-reports/hours) to the
 * TeamMemberHours shape the individual Team Hours table renders. Replaces the
 * BOLD team-hours report. Percentages fall back to a derived value when the SP
 * returns null (worker with zero actual hours).
 */
function mapHoursRowToTeamMember(r: HoursReportRow): TeamMemberHours {
  const actual = r.actualHours ?? 0
  return {
    workerId: String(r.userProfileId),
    name: r.workerName ?? '',
    workerType: r.workerType ?? '',
    engagementCode: r.engagementCode ?? '',
    billingType: r.billingType ?? '',
    status: r.status ?? '',
    planned: r.plannedHours ?? 0,
    actual,
    variance: r.varianceHours ?? 0,
    variancePercent: r.variancePercent ?? 0,
    billable: r.billableHours ?? 0,
    billablePercent: r.billableTotalPercent ?? (actual > 0 ? (r.billableHours / actual) * 100 : 0),
    nonBillable: r.nonBillableHours ?? 0,
    nonBillablePercent: r.nonBillableTotalPercent ?? (actual > 0 ? (r.nonBillableHours / actual) * 100 : 0),
  }
}


interface UseDashboardDataOptions {
  selectedEngagementId?: number
  /** When true, fetches aggregated portfolio data (no engagementId filter) using the Phoenix API */
  portfolioMode?: boolean
  /** Filter parameters forwarded to the engagement dashboard API (portfolio mode only) */
  portfolioFilters?: EngagementDashboardFilters
  /**
   * Collapse each change-request chain into one row in the engagement table, keyed on the chain
   * root, with financials summed across the chain (GW-231). Deliberately separate from
   * portfolioFilters: only the engagement table honours it, and folding it into the filter object
   * would refetch every KPI and chart on every toggle.
   */
  rollUp?: boolean
}

interface UseDashboardDataReturn extends DashboardData {
  refetch: () => void
  branchCode: string | null
  workerTypeData: WorkerTypeData[]
  workerTypeOptions: string[]
  teamHoursData: TeamHoursResponse | null
  teamHoursLoading: boolean
  teamHoursError: string | null
  portfolioTeamHoursData: PortfolioTeamHoursResponse | null
  portfolioTeamHoursLoading: boolean
  portfolioTeamHoursError: string | null
  portfolioFinanceData: PortfolioFinanceData | null
  portfolioFinanceLoading: boolean
  portfolioFinanceError: string | null
  kpiCardRows: KpiCardsRow[]
  portfolioChartTotals: { contractAmount: number; projectedCost: number }
  engagementTableData: EngagementTableRow[]
  engagementTableLoading: boolean
  engagementTableError: string | null
  utilizationData: UtilizationDataPoint[]
  utilizationFilters: UtilizationFilterOption[]
  utilizationLoading: boolean
  utilizationError: string | null
}

export function useDashboardData({
  selectedEngagementId,
  portfolioMode = false,
  portfolioFilters,
  rollUp = false,
}: UseDashboardDataOptions): UseDashboardDataReturn {
  const [engagementInfo, setEngagementInfo] = useState<DashboardData['engagementInfo']>(null)
  const [branchCode, setBranchCode] = useState<string | null>(null)
  const [engagementLoading, setEngagementLoading] = useState(false)
  const [engagementError, setEngagementError] = useState<string | null>(null)
  const [teamHoursData, setTeamHoursData] = useState<TeamHoursResponse | null>(null)
  const [teamHoursLoading, setTeamHoursLoading] = useState(false)
  const [teamHoursError, setTeamHoursError] = useState<string | null>(null)
  const [portfolioTeamHoursData, setPortfolioTeamHoursData] = useState<PortfolioTeamHoursResponse | null>(null)
  const [portfolioTeamHoursLoading, setPortfolioTeamHoursLoading] = useState(false)
  const [portfolioTeamHoursError, setPortfolioTeamHoursError] = useState<string | null>(null)
  const [utilizationData, setUtilizationData] = useState<UtilizationDataPoint[]>([])
  const [utilizationFilters, setUtilizationFilters] = useState<UtilizationFilterOption[]>([])
  const [utilizationLoading, setUtilizationLoading] = useState(false)
  const [utilizationError, setUtilizationError] = useState<string | null>(null)
  const [portfolioFinanceData, setPortfolioFinanceData] = useState<PortfolioFinanceData | null>(null)
  const [portfolioFinanceLoading, setPortfolioFinanceLoading] = useState(false)
  const [portfolioFinanceError, setPortfolioFinanceError] = useState<string | null>(null)
  const [kpiCardRows, setKpiCardRows] = useState<KpiCardsRow[]>([])
  const [monthlyChartRows, setMonthlyChartRows] = useState<MonthlyChartRow[]>([])
  const [monthlyChartLoading, setMonthlyChartLoading] = useState(false)
  const [monthlyChartError, setMonthlyChartError] = useState<string | null>(null)
  const [engagementTableData, setEngagementTableData] = useState<EngagementTableRow[]>([])
  const [engagementTableLoading, setEngagementTableLoading] = useState(false)
  const [engagementTableError, setEngagementTableError] = useState<string | null>(null)


  // Get API clients
  const engagementApi = useEngagementApi()
  const { getKpiCards, getTeamHours, getMonthlyData, getEngagementTable, getUtilization, getHoursReport } = useEngagementDashboardApi()

  // Track active refetch to enable cancellation
  const refetchAbortRef = useRef<{ cancelled: boolean } | null>(null)

  // Cancel in-flight refetch on unmount
  useEffect(() => {
    return () => {
      if (refetchAbortRef.current) {
        refetchAbortRef.current.cancelled = true
      }
    }
  }, [])

  // Shared function for fetching engagement data with cancellation support
  const fetchEngagementData = useCallback(async (
    engagementId: number,
    checkCancelled: () => boolean
  ) => {
    const searchResponse = await engagementApi.searchEngagements(
      undefined,
      `recordId eq ${engagementId}`,
      undefined,
      1,
      undefined,
      'recordId,name,engagementLead,client,startDate,endDate'
    )

    const versions = await engagementApi.getEngagementVersions(engagementId)

    // Check cancellation after async operations
    if (checkCancelled()) return null

    const searchResult = searchResponse.value?.[0]

    if (!searchResult) return null

    // Source the engagement term (start/end) from the latest ACTIVE version — the same
    // version the analytics SPs aggregate (Status not Replaced/Deleted, not a change
    // request), taking the highest version number among those. The Azure Search result
    // (searchResult) can lag behind the latest save, and its stale start/end would
    // otherwise drive effectiveFrom/effectiveTo, clamping the entire dashboard (every
    // chart + the Revenue-to-date KPI) to an outdated, shorter engagement term.
    // Fall back to the search result only if no qualifying version is found.
    const activeVersion = (versions.data ?? [])
      .map(v => v.data)
      .filter(e => e && !e.isChangeRequest && e.status !== ENGAGEMENT_STATUS.REPLACED && e.status !== ENGAGEMENT_STATUS.DELETED)
      .sort((a, b) => b.version - a.version)[0]

    return {
      branchCode: activeVersion?.coreInfo?.branchCode ?? null,
      engagementInfo: {
        client: searchResult.client || '—',
        contact: searchResult.engagementLead || '—',
        branch: '—',
        startDate: activeVersion?.coreInfo?.startDate ?? searchResult.startDate,
        endDate: activeVersion?.coreInfo?.endDate ?? searchResult.endDate,
      }
    }
  }, [engagementApi])

  // Portfolio finance fetch — calls Phoenix API (engagement.spEngagementKPICards)
  const fetchPortfolioFinanceData = useCallback(async (
    checkCancelled: () => boolean
  ): Promise<PortfolioFinanceData | null> => {
    if (!portfolioFilters) {
      throw new Error('portfolioFilters with effectiveFrom/effectiveTo are required for portfolio mode')
    }

    const rows = await getKpiCards(portfolioFilters)

    if (checkCancelled()) return null

    setKpiCardRows(rows)

    const engagements: PortfolioFinanceEngagement[] = rows.map((r: KpiCardsRow) => ({
      branch: r.branch ?? '',
      revenueRecognition: r.revenueToDate ?? 0,
      actualCostToDate: r.actualCostToDate ?? 0,
      grossProfit: (r.revenueToDate ?? 0) - (r.actualCostToDate ?? 0),
      contractAmount: r.totalContractValue ?? 0,
      unbilledRevenue: r.unbilledRevenue ?? 0,
    }))

    // Aggregate totals across all rows
    const totalRevenue = engagements.reduce((s, e) => s + e.revenueRecognition, 0)
    const totalCost = engagements.reduce((s, e) => s + e.actualCostToDate, 0)
    const totalUnbilled = engagements.reduce((s, e) => s + e.unbilledRevenue, 0)
    const totalGrossProfit = totalRevenue - totalCost

    return {
      engagements,
      totals: {
        contractAmount: engagements.reduce((s, e) => s + e.contractAmount, 0),
        projectedCost: 0,
        billedToDate: totalRevenue - totalUnbilled,
        revenueToDate: totalRevenue,
        costsToDate: totalCost,
        grossProfit: totalGrossProfit,
        unbilledRevenue: totalUnbilled,
        remainingContractAmount: 0,
        grossMarginPercent: totalRevenue > 0 ? (totalGrossProfit / totalRevenue) * 100 : 0,
      },
    }
  }, [getKpiCards, portfolioFilters])

  // Shared function for fetching team hours data with cancellation support
  const fetchTeamHoursData = useCallback(async (
    engagementId: number | null | undefined,
    checkCancelled: () => boolean
  ): Promise<TeamHoursResponse | null> => {
    if (engagementId == null) {
      throw new Error('engagementId is required for team hours data')
    }

    // Per-worker hours for this engagement from spEngagementHoursReport. Scoped
    // by engagementId only (no date window) so the table shows the engagement's
    // full team — including removed/terminated workers — with a Status per row.
    const rows = await getHoursReport({ engagementId })

    // Check cancellation after async operation
    if (checkCancelled()) return null

    return { teamMembers: rows.map(mapHoursRowToTeamMember) }
  }, [getHoursReport])

  // Portfolio lead-level hours fetch — calls Phoenix API (engagement.spEngagementLeadDashboard)
  const fetchPortfolioTeamHoursData = useCallback(async (
    checkCancelled: () => boolean
  ): Promise<PortfolioTeamHoursResponse | null> => {
    if (!portfolioFilters) {
      throw new Error('portfolioFilters with effectiveFrom/effectiveTo are required for portfolio mode')
    }

    const rows = await getTeamHours(portfolioFilters)

    if (checkCancelled()) return null

    return { leads: rows as EngagementLeadHours[] }
  }, [getTeamHours, portfolioFilters])

  // Portfolio utilization fetch — calls Phoenix API (engagement.spEngagementUtilizationDashboard)
  const fetchUtilizationData = useCallback(async (
    checkCancelled: () => boolean
  ): Promise<UtilizationResponse | null> => {
    if (!portfolioFilters) {
      throw new Error('portfolioFilters with effectiveFrom/effectiveTo are required for portfolio mode')
    }

    const response = await getUtilization(portfolioFilters)

    if (checkCancelled()) return null

    return response
  }, [getUtilization, portfolioFilters])

  // Fetch engagement metadata when engagement ID changes
  useEffect(() => {
    // Portfolio mode has no single engagement — skip metadata fetch
    if (portfolioMode) {
      setEngagementInfo(null)
      setBranchCode(null)
      setEngagementError(null)
      setEngagementLoading(false)
      return
    }

    if (!selectedEngagementId) {
      setEngagementInfo(null)
      setBranchCode(null)
      setEngagementError(null)
      setEngagementLoading(false)
      return
    }

    let isCancelled = false

    const fetchEngagement = async () => {
      setEngagementLoading(true)
      setEngagementError(null)
      try {
        const result = await fetchEngagementData(
          selectedEngagementId,
          () => isCancelled
        )

        if (!isCancelled && result) {
          setBranchCode(result.branchCode)
          setEngagementInfo(result.engagementInfo)
        } else if (!isCancelled && !result) {
          setBranchCode(null)
          setEngagementInfo(null)
        }
      } catch (error) {
        if (!isCancelled) {
          setEngagementError(error instanceof Error ? error.message : 'Failed to fetch engagement')
          setBranchCode(null)
          setEngagementInfo(null)
        }
      } finally {
        if (!isCancelled) {
          setEngagementLoading(false)
        }
      }
    }

    fetchEngagement()

    // Cleanup function: mark as cancelled when effect re-runs or unmounts
    return () => {
      isCancelled = true
    }
  }, [selectedEngagementId, portfolioMode, fetchEngagementData])

  // Fetch portfolio finance data (KPI totals, status counts, branch breakdown — portfolio mode only)
  useEffect(() => {
    if (!portfolioMode || !portfolioFilters) {
      setPortfolioFinanceData(null)
      setPortfolioFinanceError(null)
      setPortfolioFinanceLoading(false)
      setKpiCardRows([])
      return
    }

    let isCancelled = false

    const fetchPortfolioFinance = async () => {
      setPortfolioFinanceLoading(true)
      setPortfolioFinanceError(null)
      try {
        const result = await fetchPortfolioFinanceData(() => isCancelled)

        if (!isCancelled && result) {
          setPortfolioFinanceData(result)
        } else if (!isCancelled && !result) {
          setPortfolioFinanceData(null)
          setKpiCardRows([])
        }
      } catch (error) {
        if (!isCancelled) {
          setPortfolioFinanceError(error instanceof Error ? error.message : 'Failed to fetch portfolio finance data')
          setPortfolioFinanceData(null)
          setKpiCardRows([])
        }
      } finally {
        if (!isCancelled) {
          setPortfolioFinanceLoading(false)
        }
      }
    }

    fetchPortfolioFinance()

    return () => {
      isCancelled = true
    }
  }, [portfolioMode, fetchPortfolioFinanceData])

  // Fetch team hours data for individual engagements (not used in portfolio mode)
  useEffect(() => {
    if (portfolioMode || !selectedEngagementId) {
      setTeamHoursData(null)
      setTeamHoursError(null)
      setTeamHoursLoading(false)
      return
    }

    let isCancelled = false

    const fetchTeamHours = async () => {
      setTeamHoursLoading(true)
      setTeamHoursError(null)
      try {
        const result = await fetchTeamHoursData(
          selectedEngagementId,
          () => isCancelled
        )

        if (!isCancelled && result) {
          setTeamHoursData(result)
        } else if (!isCancelled && !result) {
          setTeamHoursData(null)
        }
      } catch (error) {
        if (!isCancelled) {
          setTeamHoursError(error instanceof Error ? error.message : 'Failed to fetch team hours data')
          setTeamHoursData(null)
        }
      } finally {
        if (!isCancelled) {
          setTeamHoursLoading(false)
        }
      }
    }

    fetchTeamHours()

    // Cleanup function: mark as cancelled when effect re-runs or unmounts
    return () => {
      isCancelled = true
    }
  }, [selectedEngagementId, portfolioMode, fetchTeamHoursData])


  // Fetch portfolio lead-level hours (only in portfolio mode)
  useEffect(() => {
    if (!portfolioMode || !portfolioFilters) {
      setPortfolioTeamHoursData(null)
      setPortfolioTeamHoursError(null)
      setPortfolioTeamHoursLoading(false)
      return
    }

    let isCancelled = false

    const fetchPortfolioHours = async () => {
      setPortfolioTeamHoursLoading(true)
      setPortfolioTeamHoursError(null)
      try {
        const result = await fetchPortfolioTeamHoursData(() => isCancelled)

        if (!isCancelled && result) {
          setPortfolioTeamHoursData(result)
        } else if (!isCancelled && !result) {
          setPortfolioTeamHoursData(null)
        }
      } catch (error) {
        if (!isCancelled) {
          setPortfolioTeamHoursError(error instanceof Error ? error.message : 'Failed to fetch portfolio team hours data')
          setPortfolioTeamHoursData(null)
        }
      } finally {
        if (!isCancelled) {
          setPortfolioTeamHoursLoading(false)
        }
      }
    }

    fetchPortfolioHours()

    return () => {
      isCancelled = true
    }
  }, [portfolioMode, fetchPortfolioTeamHoursData])

  // Fetch utilization data (portfolio mode only — separate SP with weekly granularity)
  useEffect(() => {
    if (!portfolioMode || !portfolioFilters) {
      setUtilizationData([])
      setUtilizationFilters([])
      setUtilizationError(null)
      setUtilizationLoading(false)
      return
    }

    let isCancelled = false

    const fetchUtilization = async () => {
      setUtilizationLoading(true)
      setUtilizationError(null)
      try {
        const result = await fetchUtilizationData(() => isCancelled)

        if (!isCancelled && result) {
          setUtilizationData(result.utilizationData ?? [])
          setUtilizationFilters(
            (result.utilizationFilters ?? []).map(f => ({
              ...f,
              displayLabel: f.procurementSourceName
                ? `${f.workerProfileTypeName} – ${f.procurementSourceName}`
                : f.workerProfileTypeName ?? 'Unknown',
            }))
          )
        } else if (!isCancelled && !result) {
          setUtilizationData([])
          setUtilizationFilters([])
        }
      } catch (error) {
        if (!isCancelled) {
          setUtilizationError(error instanceof Error ? error.message : 'Failed to fetch utilization data')
          setUtilizationData([])
          setUtilizationFilters([])
        }
      } finally {
        if (!isCancelled) setUtilizationLoading(false)
      }
    }

    fetchUtilization()

    return () => { isCancelled = true }
  }, [portfolioMode, fetchUtilizationData])

  // Fetch monthly chart data from spEngagementMonthlyDashboard.
  // Runs in both portfolio mode (with caller-supplied filters) and individual
  // mode (filters derived via buildIndividualMonthlyFilters, which normalizes
  // the search response's full-ISO datetimes to YYYY-MM-DD).
  useEffect(() => {
    const filters: EngagementDashboardFilters | null = portfolioMode
      ? (portfolioFilters ?? null)
      : buildIndividualMonthlyFilters(selectedEngagementId, engagementInfo)

    if (!filters) {
      setMonthlyChartRows([])
      setMonthlyChartError(null)
      setMonthlyChartLoading(false)
      return
    }

    let isCancelled = false

    const fetchMonthlyChart = async () => {
      setMonthlyChartLoading(true)
      setMonthlyChartError(null)
      try {
        const rows = await getMonthlyData(filters)
        if (!isCancelled) setMonthlyChartRows(rows)
      } catch (error) {
        if (!isCancelled) {
          setMonthlyChartError(error instanceof Error ? error.message : 'Failed to fetch monthly chart data')
          setMonthlyChartRows([])
        }
      } finally {
        if (!isCancelled) setMonthlyChartLoading(false)
      }
    }

    fetchMonthlyChart()
    return () => { isCancelled = true }
  }, [portfolioMode, portfolioFilters, selectedEngagementId, engagementInfo?.startDate, engagementInfo?.endDate, getMonthlyData])

  // Fetch portfolio engagement table data
  useEffect(() => {
    if (!portfolioMode || !portfolioFilters) {
      setEngagementTableData([])
      setEngagementTableError(null)
      setEngagementTableLoading(false)
      return
    }

    let isCancelled = false

    const fetchEngagementTableRows = async () => {
      setEngagementTableLoading(true)
      setEngagementTableError(null)
      try {
        const rows = await getEngagementTable({ ...portfolioFilters, rollUp })
        const unique = rows.filter((r, i, arr) => arr.findIndex(x => x.id === r.id) === i)
        if (!isCancelled) setEngagementTableData(unique)
      } catch (error) {
        if (!isCancelled) {
          setEngagementTableError(error instanceof Error ? error.message : 'Failed to fetch engagement table data')
          setEngagementTableData([])
        }
      } finally {
        if (!isCancelled) setEngagementTableLoading(false)
      }
    }

    fetchEngagementTableRows()
    return () => { isCancelled = true }
  }, [portfolioMode, portfolioFilters, rollUp, getEngagementTable])

  // Calculate all metrics from spEngagementMonthlyDashboard rows.
  // contractAmount/projectedCost are constant per engagement — read from any row.
  // Revenue/cost/billed totals are summed across all rows (the SP places revenue on
  // engagement-level NULL-worker rows and cost on worker rows, so a flat sum counts
  // each value exactly once).
  const metrics = useMemo(() => {
    const firstRow = monthlyChartRows[0]
    const contractAmount = firstRow?.contractAmount ?? 0
    const projectedCost = firstRow?.projectedCost ?? 0

    // "To date" KPIs must stop at the end of the last completed month. The Rev Rec
    // schedule is fully populated across the entire engagement term, so
    // monthlyChartRows includes the current (in-progress) month and future months
    // (the fetch window runs to the engagement end date so the time-series charts
    // can still plot the full/planned timeline). Summing every row would report
    // total contract revenue instead of revenue recognized to date — cap the sum at
    // the last fully-completed month, i.e. exclude the current calendar month and
    // everything after it. row.month is an ISO "YYYY-MM-DD" first-of-month string;
    // compare the YYYY-MM prefix lexically against the local current month
    // (parseDateOnly treats SP dates as local, so we derive "now" the same way to
    // avoid a month-boundary TZ shift).
    const now = new Date()
    const currentMonthYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

    let revenueToDate = 0
    let costsToDate = 0
    let billedToDate = 0
    for (const row of monthlyChartRows) {
      if (row.month.slice(0, 7) >= currentMonthYm) continue // skip current + future months
      revenueToDate += row.monthlyRevenueRecognition ?? 0
      costsToDate   += row.monthlyCost ?? 0
      billedToDate  += row.monthlyBilled ?? 0
    }

    const grossProfit = revenueToDate - costsToDate
    const unbilledRevenue = revenueToDate - billedToDate
    const grossMarginPercent = revenueToDate > 0 ? (grossProfit / revenueToDate) * 100 : 0
    const billedPercent = contractAmount > 0 ? (billedToDate / contractAmount) * 100 : 0
    const remainingContract = contractAmount - billedToDate

    return {
      contractAmount,
      projectedCost,
      billedToDate,
      revenueToDate,
      costsToDate,
      grossProfit,
      unbilledRevenue,
      grossMarginPercent,
      billedPercent,
      remainingContract,
      avgMargin: grossMarginPercent,
      revenueTrend: null, // Will be calculated from monthly data
    }
  }, [monthlyChartRows])

  // Aggregate per-engagement × month × worker rows from spEngagementMonthlyDashboard
  // into one row per month for the time-series charts. Used in both portfolio and
  // individual mode.
  const monthlyData = useMemo(() => {
    const byMonth = new Map<string, { revenue: number; costs: number; billed: number; grossProfit: number }>()
    for (const row of monthlyChartRows) {
      const key = row.month
      const existing = byMonth.get(key) ?? { revenue: 0, costs: 0, billed: 0, grossProfit: 0 }
      byMonth.set(key, {
        revenue: existing.revenue + (row.monthlyRevenueRecognition ?? 0),
        costs: existing.costs + (row.monthlyCost ?? 0),
        billed: existing.billed + (row.monthlyBilled ?? 0),
        grossProfit: existing.grossProfit + (row.monthlyGrossProfit ?? 0),
      })
    }
    return Array.from(byMonth.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([monthDate, agg]) => ({
        // Parse via parseDateOnly so the SP's YYYY-MM-DD string is treated as
        // a local date — new Date(monthDate) interprets it as UTC midnight,
        // which shifts every label back by a month in non-UTC timezones.
        month: parseDateOnly(monthDate)?.toLocaleString('default', { month: 'short' }) ?? '',
        monthDate,
        revenue: agg.revenue,
        costs: agg.costs,
        billed: agg.billed,
        grossProfit: agg.grossProfit,
      }))
  }, [monthlyChartRows])

  // Calculate revenue trend from monthly data
  const revenueTrend = useMemo(() => {
    return calculateRevenueTrend(monthlyData)
  }, [monthlyData])

  // Update metrics with trend
  const metricsWithTrend = useMemo(() => ({
    ...metrics,
    revenueTrend,
  }), [metrics, revenueTrend])

  // Refetch all data
  const refetch = useCallback(async () => {
    if (!portfolioMode && !selectedEngagementId) return

    // Cancel any previous refetch in progress
    if (refetchAbortRef.current) {
      refetchAbortRef.current.cancelled = true
    }

    // Create new cancel token for this refetch
    const cancelToken = { cancelled: false }
    refetchAbortRef.current = cancelToken
    const checkCancelled = () => cancelToken.cancelled

    // Refetch engagement metadata (skip in portfolio mode)
    if (!portfolioMode && selectedEngagementId) {
      setEngagementLoading(true)
      setEngagementError(null)
      try {
        const result = await fetchEngagementData(selectedEngagementId, checkCancelled)

        if (!checkCancelled() && result) {
          setBranchCode(result.branchCode)
          setEngagementInfo(result.engagementInfo)
        } else if (!checkCancelled() && !result) {
          setBranchCode(null)
          setEngagementInfo(null)
        }
      } catch (error) {
        if (!checkCancelled()) {
          setEngagementError(error instanceof Error ? error.message : 'Failed to fetch engagement')
          setBranchCode(null)
          setEngagementInfo(null)
        }
      } finally {
        if (!checkCancelled()) {
          setEngagementLoading(false)
        }
      }
    }

    if (portfolioMode && portfolioFilters) {
      // Refetch portfolio finance data (KPI totals / status counts / branch margin)
      setPortfolioFinanceLoading(true)
      setPortfolioFinanceError(null)
      try {
        const result = await fetchPortfolioFinanceData(checkCancelled)

        if (!checkCancelled() && result) {
          setPortfolioFinanceData(result)
        } else if (!checkCancelled() && !result) {
          setPortfolioFinanceData(null)
        }
      } catch (error) {
        if (!checkCancelled()) {
          setPortfolioFinanceError(error instanceof Error ? error.message : 'Failed to fetch portfolio finance data')
          setPortfolioFinanceData(null)
        }
      } finally {
        if (!checkCancelled()) {
          setPortfolioFinanceLoading(false)
        }
      }

      // Refetch portfolio lead-level hours
      setPortfolioTeamHoursLoading(true)
      setPortfolioTeamHoursError(null)
      try {
        const result = await fetchPortfolioTeamHoursData(checkCancelled)

        if (!checkCancelled() && result) {
          setPortfolioTeamHoursData(result)
        } else if (!checkCancelled() && !result) {
          setPortfolioTeamHoursData(null)
        }
      } catch (error) {
        if (!checkCancelled()) {
          setPortfolioTeamHoursError(error instanceof Error ? error.message : 'Failed to fetch portfolio team hours data')
          setPortfolioTeamHoursData(null)
        }
      } finally {
        if (!checkCancelled()) {
          setPortfolioTeamHoursLoading(false)
        }
      }
    } else {
      // Refetch individual engagement team hours
      setTeamHoursLoading(true)
      setTeamHoursError(null)
      try {
        const result = await fetchTeamHoursData(selectedEngagementId, checkCancelled)

        if (!checkCancelled() && result) {
          setTeamHoursData(result)
        } else if (!checkCancelled() && !result) {
          setTeamHoursData(null)
        }
      } catch (error) {
        if (!checkCancelled()) {
          setTeamHoursError(error instanceof Error ? error.message : 'Failed to fetch team hours data')
          setTeamHoursData(null)
        }
      } finally {
        if (!checkCancelled()) {
          setTeamHoursLoading(false)
        }
      }
    }
    // Refetch monthly chart data in both modes. In portfolio mode use the
    // caller-supplied filters; in individual mode derive filters via the
    // same helper as the initial fetch effect.
    const monthlyFilters: EngagementDashboardFilters | null = portfolioMode
      ? (portfolioFilters ?? null)
      : buildIndividualMonthlyFilters(selectedEngagementId, engagementInfo)

    if (monthlyFilters) {
      setMonthlyChartLoading(true)
      setMonthlyChartError(null)
      try {
        const rows = await getMonthlyData(monthlyFilters)
        if (!checkCancelled()) setMonthlyChartRows(rows)
      } catch (error) {
        if (!checkCancelled()) {
          setMonthlyChartError(error instanceof Error ? error.message : 'Failed to fetch monthly chart data')
          setMonthlyChartRows([])
        }
      } finally {
        if (!checkCancelled()) setMonthlyChartLoading(false)
      }
    }

    if (portfolioMode && portfolioFilters) {
      // Refetch engagement table data (portfolio-only)
      setEngagementTableLoading(true)
      setEngagementTableError(null)
      try {
        const rows = await getEngagementTable({ ...portfolioFilters, rollUp })
        const unique = rows.filter((r, i, arr) => arr.findIndex(x => x.id === r.id) === i)
        if (!checkCancelled()) setEngagementTableData(unique)
      } catch (error) {
        if (!checkCancelled()) {
          setEngagementTableError(error instanceof Error ? error.message : 'Failed to fetch engagement table data')
          setEngagementTableData([])
        }
      } finally {
        if (!checkCancelled()) setEngagementTableLoading(false)
      }
    }
  }, [selectedEngagementId, portfolioMode, portfolioFilters, rollUp, engagementInfo?.startDate, engagementInfo?.endDate, fetchEngagementData, fetchTeamHoursData, fetchPortfolioTeamHoursData, fetchPortfolioFinanceData, getMonthlyData, getEngagementTable])

  // Combine loading and error states — split by mode so portfolio callers don't see individual-mode states.
  // All analytics are now SP-backed (BOLD fully removed from this dashboard).
  // Same reasoning as `error` below: this aggregate gates the KPI cards and charts, and the three
  // tables each take their own loading prop. Including a table's loading state here blanked the top
  // half of the page behind a spinner whenever that table refetched - most visibly on every roll-up
  // toggle, which refetches only the engagement table.
  const isLoading = portfolioMode
    ? portfolioFinanceLoading || monthlyChartLoading
    : engagementLoading || monthlyChartLoading || teamHoursLoading
  // engagementTableError is deliberately NOT folded in here. The page treats this aggregate as fatal
  // and replaces the whole dashboard with a single line of red text, so a failure of one panel took
  // down the KPI cards, all six charts, utilization and team hours alongside it - every one of which
  // had loaded fine. The engagement table already receives its own error prop and renders a proper
  // error state inside its card, the same way portfolioFinanceError is handled inline rather than
  // fatally. It also has to stay non-fatal for the roll-up toggle to be recoverable: that control
  // lives in the table's card header, so replacing the page removes the only way to switch roll-up
  // back off, leaving a reload as the only escape.
  //
  // portfolioTeamHoursError is out for the same reason: PortfolioTeamHoursTable takes an error prop
  // and renders it inside its own card, so folding it in here left that card's error state as dead
  // code and let a single slow SP replace a page that had otherwise loaded completely.
  //
  // utilizationError stays fatal, and that is not an oversight - UtilizationChart takes no error
  // prop, so there is no inline state for the aggregate to shadow. Give it one and it should move
  // out of here too.
  const error = portfolioMode
    ? monthlyChartError || utilizationError
    : engagementError || monthlyChartError || teamHoursError

  // Accumulate all known worker types seen across fetches (survives filter changes)
  const knownWorkerTypes = useRef(new Set<string>())
  useMemo(() => {
    if (!portfolioMode) return
    for (const row of monthlyChartRows) {
      if (row.workerType) knownWorkerTypes.current.add(row.workerType)
    }
  }, [portfolioMode, monthlyChartRows])
  const workerTypeOptions = Array.from(knownWorkerTypes.current).sort()

  // Bucket cost by whatever worker-type string the SP returns. Values come from
  // CodeProfileType.Value and are trusted as-is — no allow-list — so categories
  // like W2 Worker, US Inc Worker, Canadian SP, Subvendor worker don't get
  // silently dropped from the pie.
  const workerTypeData = useMemo(() => {
    const byCostType = new Map<string, number>()
    for (const row of monthlyChartRows) {
      const type = row.workerType?.trim()
      if (type) {
        byCostType.set(type, (byCostType.get(type) ?? 0) + (row.monthlyCost ?? 0))
      }
    }
    const total = Array.from(byCostType.values()).reduce((s, v) => s + v, 0)
    return Array.from(byCostType.entries()).map(([name, costs]) => ({
      name,
      costs,
      value: total > 0 ? (costs / total) * 100 : 0,
    }))
  }, [monthlyChartRows])

  const portfolioChartTotals = useMemo(() => {
    const seen = new Set<number>()
    let contractAmount = 0
    let projectedCost = 0
    for (const row of monthlyChartRows) {
      if (!seen.has(row.id)) {
        seen.add(row.id)
        contractAmount += row.contractAmount
        projectedCost += row.projectedCost
      }
    }
    return { contractAmount, projectedCost }
  }, [monthlyChartRows])

  return {
    metrics: metricsWithTrend,
    monthlyData,
    engagementInfo,
    isLoading,
    error,
    refetch,
    branchCode,
    workerTypeData,
    workerTypeOptions,
    teamHoursData,
    teamHoursLoading,
    teamHoursError,
    portfolioTeamHoursData,
    portfolioTeamHoursLoading,
    portfolioTeamHoursError,
    portfolioFinanceData,
    portfolioFinanceLoading,
    portfolioFinanceError,
    kpiCardRows,
    portfolioChartTotals,
    engagementTableData,
    engagementTableLoading,
    engagementTableError,
    utilizationData,
    utilizationFilters,
    utilizationLoading,
    utilizationError,
  }
}

